import React, { useEffect, useRef, useState } from "react";
import { Upload, Link2, Loader2, Trash2, Video, Image as ImageIcon, Plus, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * GreenScreenAdminTab — curate the green-screen background library shown
 * to all creators in the GreenScreenToggle component.
 *
 * Two upload paths:
 *   1. UPLOAD from device — image OR video ≤ 10MB, video ≤ 30s
 *   2. LINK paste — image / video URL hosted anywhere (Unsplash, S3,
 *      CDN, YouTube thumb etc.). Links are preferred for larger files.
 *
 * Items land in `greenscreen_backgrounds` via the admin-gated
 * `/api/greenscreen/backgrounds` endpoint.
 */
const MAX_VIDEO_SEC = 30;
const MAX_UPLOAD_MB = 10;

export default function GreenScreenAdminTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("image"); // image | video
  const [source, setSource] = useState("upload"); // upload | link | ai
  const [linkUrl, setLinkUrl] = useState("");
  const [uploadDataUrl, setUploadDataUrl] = useState("");
  const [uploadDuration, setUploadDuration] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/greenscreen/backgrounds");
      setItems(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const isVid = f.type.startsWith("video/");
    const isImg = f.type.startsWith("image/");
    if (!isVid && !isImg) { toast.error("Pick image or video"); return; }
    if (f.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(`File > ${MAX_UPLOAD_MB}MB — use a Link instead`);
      return;
    }
    try {
      if (isVid) {
        const d = await probeDuration(f);
        if (!Number.isFinite(d) || d <= 0) throw new Error("bad video");
        if (d > MAX_VIDEO_SEC + 0.5) {
          toast.error(`Video is ${d.toFixed(1)}s — max ${MAX_VIDEO_SEC}s`);
          return;
        }
        setUploadDuration(Math.round(d * 1000));
      } else {
        setUploadDuration(null);
      }
      const dataUrl = await readAsDataUrl(f);
      setKind(isVid ? "video" : "image");
      setUploadDataUrl(dataUrl);
      if (!title) setTitle(f.name.replace(/\.[^.]+$/, "").slice(0, 60));
    } catch {
      toast.error("Couldn't read file");
    }
  };

  const submit = async () => {
    if (!title.trim()) { toast.error("Add a title"); return; }
    if (source === "ai") {
      // AI flow has its own button — submit() falls through to it.
      return generate();
    }
    if (source === "upload" && !uploadDataUrl) { toast.error("Pick a file"); return; }
    if (source === "link" && !/^https?:\/\//i.test(linkUrl)) { toast.error("Paste http/https URL"); return; }
    const payload = {
      title: title.trim(),
      kind,
      source,
      data_url: source === "upload" ? uploadDataUrl : null,
      url: source === "link" ? linkUrl.trim() : null,
      duration_ms: kind === "video" ? (uploadDuration || 15000) : null,
      is_published: true,
    };
    setSaving(true);
    try {
      const { data } = await api.post("/greenscreen/backgrounds", payload);
      setItems((prev) => [data, ...prev]);
      setTitle(""); setLinkUrl(""); setUploadDataUrl(""); setUploadDuration(null);
      toast.success(`${payload.kind === "video" ? "Video" : "Image"} added`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  /**
   * Kick off Gemini Nano Banana on the backend. Round-trip is usually
   * 6–15s so we show a spinner + persistent toast and disable the
   * button. The prompt is server-wrapped to bias toward 16:9 backdrops
   * (no centred subject), so admins don't need to remember to specify.
   */
  const generate = async () => {
    if (!title.trim()) { toast.error("Add a title"); return; }
    if (aiPrompt.trim().length < 4) { toast.error("Prompt is too short"); return; }
    setGenerating(true);
    const tid = toast.loading("Conjuring backdrop with Nano Banana…");
    try {
      const { data } = await api.post("/greenscreen/backgrounds/generate", {
        title: title.trim(),
        prompt: aiPrompt.trim(),
        is_published: true,
      });
      setItems((prev) => [data, ...prev]);
      setTitle(""); setAiPrompt("");
      toast.success("AI background published", { id: tid });
    } catch (e) { toast.error(formatApiError(e), { id: tid }); }
    finally { setGenerating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this background?")) return;
    try {
      await api.delete(`/greenscreen/backgrounds/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Toggle the `featured` flag — "Brand pack" status. Featured items
  // are sorted first in every creator's mobile/desktop GS carousel,
  // letting us curate a Tap In TV-branded set of backgrounds (Studio A,
  // Newsdesk, Concert, etc.) that always show up at the head.
  const toggleFeatured = async (id) => {
    try {
      const { data } = await api.post(`/greenscreen/backgrounds/${id}/feature`);
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, featured: !!data.featured } : it));
      toast.success(data.featured ? "Added to brand pack" : "Removed from brand pack");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // One-click "Generate Tap In TV Brand Pack" — POSTs to the backend
  // seeder which spins up 6 Nano Banana image gens with curated prompts
  // (Studio A, Newsdesk, Concert, Talk Show, Sports Desk, DJ Booth)
  // and inserts them into the library with `featured: true` so they
  // appear at the head of every creator's carousel immediately.
  const [seedingBrandPack, setSeedingBrandPack] = useState(false);
  const seedBrandPack = async () => {
    if (seedingBrandPack) return;
    if (!window.confirm(
      "Generate Tap In TV Brand Pack? This creates 6 on-brand AI backgrounds (~30s) and marks them all as featured. Existing brand pack tiles will be reused if present."
    )) return;
    setSeedingBrandPack(true);
    try {
      const { data } = await api.post("/greenscreen/backgrounds/seed-brand-pack", null, { timeout: 180000 });
      if (data?.skipped) {
        toast.info("Brand pack already exists — no new tiles created.");
      } else {
        toast.success(`Brand pack ready — ${data?.created || 0} new tiles added.`);
      }
      // Refresh the list so the new tiles appear with the gold star.
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't seed brand pack — check the LLM key.");
    } finally {
      setSeedingBrandPack(false);
    }
  };

  // Seasonal AI packs — 4 themed tiles per pack (Holiday, Sports Night,
  // Award Show, Lo-fi Chill). Idempotent per-title on the server.
  const [seedingPack, setSeedingPack] = useState(null);
  const seedPack = async (key, label) => {
    if (seedingPack) return;
    if (!window.confirm(`Generate the ${label} pack? Creates 4 AI backgrounds (~20s). Existing tiles are skipped.`)) return;
    setSeedingPack(key);
    try {
      const { data } = await api.post("/greenscreen/backgrounds/seed-pack", { pack: key }, { timeout: 180000 });
      toast.success(`${label}: ${data?.created || 0} new tile${data?.created === 1 ? "" : "s"}${data?.skipped ? `, ${data.skipped} already existed` : ""}.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't generate pack — check the LLM key.");
    } finally {
      setSeedingPack(null);
    }
  };

  return (
    <section data-testid="gs-admin-tab" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-display font-bold text-white">Green-Screen Library</h2>
          <p className="text-sm text-white/60 mt-1">
            Images or short videos (≤{MAX_VIDEO_SEC}s) that creators can use as
            virtual backgrounds when they enable green-screen mode.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            type="button"
            onClick={seedBrandPack}
            disabled={seedingBrandPack}
            data-testid="gs-admin-seed-brand-pack"
            className={`inline-flex items-center gap-2 font-bold text-xs uppercase tracking-wider px-3 py-2 rounded-full border ${
              seedingBrandPack
                ? "bg-white/5 text-white/40 border-white/10 cursor-wait"
                : "bg-tv-accent text-black border-tv-accent hover:bg-yellow-300 shadow-[0_8px_24px_rgba(245,196,69,0.35)]"
            }`}
            title="Generate 6 on-brand Tap In TV backgrounds via AI and mark them featured"
          >
            {seedingBrandPack ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Star className="w-3.5 h-3.5" />}
            {seedingBrandPack ? "Generating brand pack…" : "Generate Tap In Brand Pack"}
          </button>
          <div className="flex flex-wrap gap-1.5 justify-end" data-testid="gs-admin-seasonal-row">
            {[["holiday", "Holiday"], ["sports", "Sports Night"], ["awards", "Award Show"], ["lofi", "Lo-fi Chill"]].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => seedPack(k, label)}
                disabled={!!seedingPack}
                data-testid={`gs-admin-seed-pack-${k}`}
                className={`inline-flex items-center gap-1 font-bold text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded-full border ${
                  seedingPack === k
                    ? "bg-white/5 text-white/40 border-white/10 cursor-wait"
                    : "bg-white/5 text-white/75 border-white/15 hover:bg-white/10 hover:text-white"
                }`}
                title={`Generate 4 AI backgrounds for the ${label} pack`}
              >
                {seedingPack === k ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Create form */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3" data-testid="gs-admin-form">
        <div className="flex items-center gap-2">
          <Plus className="w-4 h-4 text-tv-accent" />
          <div className="text-white font-black text-sm uppercase tracking-wider">Add background</div>
        </div>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (e.g. Sunset Beach)"
          data-testid="gs-admin-title"
          className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white outline-none focus:border-tv-accent"
          maxLength={80}
        />
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-white/50 uppercase tracking-wider font-bold">Source</span>
          {["upload", "link", "ai"].map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`gs-admin-source-${s}`}
              onClick={() => {
                setSource(s);
                // AI generates stills only — auto-flip kind so the
                // Type chips render the correct active state.
                if (s === "ai") setKind("image");
              }}
              className={`px-3 py-1 rounded-full uppercase tracking-wider font-black text-[10px] inline-flex items-center gap-1 ${
                source === s ? "bg-tv-accent text-black" : "bg-white/10 text-white/70 hover:bg-white/15"
              }`}
            >
              {s === "ai" && <Sparkles className="w-3 h-3" />}
              {s === "upload" ? "Upload" : s === "link" ? "Link" : "AI Generate"}
            </button>
          ))}
          <span className="text-white/50 uppercase tracking-wider font-bold ml-3">Type</span>
          {["image", "video"].map((k) => (
            <button
              key={k}
              type="button"
              data-testid={`gs-admin-kind-${k}`}
              onClick={() => setKind(k)}
              disabled={source === "ai" && k === "video"}
              title={source === "ai" && k === "video" ? "AI generates still images only" : ""}
              className={`px-3 py-1 rounded-full uppercase tracking-wider font-black text-[10px] inline-flex items-center gap-1 ${
                kind === k ? "bg-tv-accent text-black" : "bg-white/10 text-white/70 hover:bg-white/15"
              } disabled:opacity-30 disabled:cursor-not-allowed`}
            >
              {k === "image" ? <ImageIcon className="w-3 h-3" /> : <Video className="w-3 h-3" />}
              {k}
            </button>
          ))}
        </div>

        {source === "upload" && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="gs-admin-upload-btn"
              className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/15 text-white text-xs font-bold px-3 py-2 rounded"
            >
              <Upload className="w-3.5 h-3.5" /> Pick file
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*"
              onChange={pickFile}
              className="hidden"
              data-testid="gs-admin-upload-input"
            />
            {uploadDataUrl && (
              <span className="text-[11px] text-white/60" data-testid="gs-admin-upload-ready">
                Ready{uploadDuration ? ` · ${Math.round(uploadDuration/1000)}s` : ""}
              </span>
            )}
          </div>
        )}

        {source === "link" && (
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-white/50" />
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com/bg.jpg or .mp4"
              data-testid="gs-admin-link-input"
              className="flex-1 bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white outline-none focus:border-tv-accent"
            />
          </div>
        )}

        {source === "ai" && (
          <div className="space-y-2" data-testid="gs-admin-ai-row">
            <div className="flex items-center gap-2 text-[11px] text-white/55">
              <Sparkles className="w-3 h-3 text-tv-accent" />
              Generated stills are 16:9, host-friendly (no centred subject), via Gemini Nano Banana.
            </div>
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="e.g. Neon cyberpunk newsroom at night, holographic monitors, rain on the windows…"
              data-testid="gs-admin-ai-prompt"
              maxLength={600}
              rows={3}
              className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white outline-none focus:border-tv-accent resize-none"
            />
            <div className="text-[10px] text-white/40 tabular-nums text-right">
              {aiPrompt.length}/600
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={saving || generating}
          data-testid={source === "ai" ? "gs-admin-generate" : "gs-admin-submit"}
          className="bg-tv-accent hover:bg-yellow-400 text-black font-black uppercase tracking-wider text-xs px-4 py-2 rounded-full disabled:opacity-50 inline-flex items-center gap-2"
        >
          {(saving || generating) ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : source === "ai" ? (
            <Sparkles className="w-3.5 h-3.5" />
          ) : (
            <Plus className="w-3.5 h-3.5" />
          )}
          {source === "ai" ? (generating ? "Generating…" : "Generate & publish") : "Publish to library"}
        </button>
      </div>

      {/* Library grid */}
      <div>
        <div className="text-[10px] uppercase tracking-wider font-black text-white/50 mb-2">
          Library ({items.length})
        </div>
        {loading && <div className="text-white/50 text-sm"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-white/40 italic text-sm" data-testid="gs-admin-empty">No backgrounds yet.</div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`gs-admin-item-${it.id}`}
              className="relative aspect-video rounded border border-white/10 overflow-hidden bg-black group"
            >
              {it.kind === "video" ? (
                <video
                  src={it.data_url || it.url}
                  className="w-full h-full object-cover"
                  muted
                  loop
                  playsInline
                  autoPlay
                />
              ) : (
                <img
                  src={it.data_url || it.url}
                  alt={it.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              )}
              <div className="absolute top-1 left-1 bg-black/70 text-white text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded flex items-center gap-1">
                {it.kind === "video" ? <Video className="w-2.5 h-2.5" /> : <ImageIcon className="w-2.5 h-2.5" />}
                {it.kind}
                {it.kind === "video" && it.duration_ms && <span>· {Math.round(it.duration_ms / 1000)}s</span>}
              </div>
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent text-white text-[10px] font-bold px-2 py-1 truncate">
                {it.title}
              </div>
              <button
                type="button"
                data-testid={`gs-admin-feature-${it.id}`}
                onClick={() => toggleFeatured(it.id)}
                className={`absolute top-1 right-8 rounded p-1 transition opacity-0 group-hover:opacity-100 ${
                  it.featured ? "bg-tv-accent text-black opacity-100" : "bg-black/70 hover:bg-tv-accent hover:text-black text-white"
                }`}
                title={it.featured ? "Remove from Tap In brand pack" : "Add to Tap In brand pack — shows first in creator carousels"}
              >
                <Star className={`w-3 h-3 ${it.featured ? "fill-current" : ""}`} />
              </button>
              <button
                type="button"
                data-testid={`gs-admin-delete-${it.id}`}
                onClick={() => remove(it.id)}
                className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition"
                title="Delete"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(file);
  });
}
function probeDuration(file) {
  return new Promise((resolve, reject) => {
    const v = document.createElement("video");
    const blob = URL.createObjectURL(file);
    v.preload = "metadata";
    v.muted = true;
    v.src = blob;
    const done = (d) => { URL.revokeObjectURL(blob); resolve(d); };
    v.onloadedmetadata = () => done(v.duration);
    v.onerror = () => { URL.revokeObjectURL(blob); reject(v.error); };
    setTimeout(() => done(0), 4000);
  });
}
